import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Download } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { employeeAsync } from '../../performance-actions/ActionFormDrawer';
import { Badge } from '../../performance-operations/styles';
import { Hidden, Note } from '../../../components/performance/evaluation/EvaluationParts';
import {
  CALIBRATION_PATH, REVIEW_GAPS_PATH, downloadBlob, formatNum, formatSigned, withFilters,
} from '../../../components/performance/evaluation/evaluationAnalytics';
import { NS, SORTABLE, createReviewRecord } from '../config';

// E07 alt alan — yönetici tablosu: ekip n, karşılaştırma grubu (ünvan grubu + kapsama), puan ortalaması, benzer grup, M22 sapma
// (küçük örneklemde "Yetersiz Örneklem" — sıfır değil), M23, M24 sıkışma, bantlar, M25, geri bildirim kapsaması, M05 ve gecikme,
// inceleme durumu ve sinyaller. YÖNETİCİLER SIRALANMAZ: yalnız ad ve ekip n'i sıralanabilir (metrik sütunları sıralanamaz; sunucu
// metrik sıralamasını 400 ile reddeder), sıra/rütbe sütunu yoktur. Satır → E06 ekip farkları | E08 kalibrasyon | inceleme kaydı.
const Wrap = styled.div`
  grid-column: span 12;
  min-width: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const REVIEW_TONE = { none: undefined, open: 'info', done: 'success' };

export default function ManagersTable({
  filters, panel, table, onTable, exportFn, calibrationApplicable,
}) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language;
  const [reviewFor, setReviewFor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const rows = panel.data?.data || [];
  const total = panel.data?.totalRowCount || 0;

  const onTableChanged = (action, state) => {
    const sortName = state.sortOrder?.name;
    onTable({
      pageNumber: (state.page || 0) + 1,
      pageSize: state.rowsPerPage || 10,
      sort: sortName ? SORTABLE[sortName] || null : null,
      order: state.sortOrder?.direction || 'asc',
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFn();
      downloadBlob(res, 'yonetici-degerlendirme-davranisi.xlsx');
    } finally {
      setExporting(false);
    }
  };

  const reviewInitial = useMemo(() => (reviewFor ? {
    title: t(`${NS}.review.defaultTitle`, { name: reviewFor.managerName }),
    ownerEmployeeId: null,
    dueDate: '',
    description: '',
  } : undefined), [reviewFor, t]);

  const reviewSchema = [
    { name: 'title', label: t(`${NS}.review.titleField`), type: 'text', required: true, maxLength: 200 },
    { name: 'ownerEmployeeId', label: t(`${NS}.review.owner`), type: 'asyncSelect', required: true, onSearch: employeeAsync.onSearch, onGetById: employeeAsync.onGetById },
    { name: 'dueDate', label: t(`${NS}.review.dueDate`), type: 'date', required: true },
    { name: 'description', label: t(`${NS}.review.description`), type: 'textarea', maxLength: 2000, rows: 4 },
  ];

  const rowOf = (meta) => rows[meta.rowIndex] || {};
  const dash = '-';
  const hiddenOr = (r, v, render) => {
    if (v === null || v === undefined) return r.teamN ? <Hidden ns={NS} /> : dash;
    return render(v);
  };
  const columns = [
    { name: 'managerName', label: t(`${NS}.col.manager`), options: { sort: true, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'teamN', label: t(`${NS}.col.teamN`), options: { sort: true, filter: false, customBodyRender: (v) => formatNum(v, lang, 0) } },
    {
      name: 'comparisonGroups',
      label: t(`${NS}.col.comparison`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (!r.teamN) return dash;
          return t(`${NS}.comparison.value`, { groups: formatNum(v, lang, 0), coverage: formatNum(r.comparisonCoverage, lang) });
        },
      },
    },
    { name: 'teamAverage', label: t(`${NS}.col.teamAverage`), options: { sort: false, filter: false, customBodyRender: (v, meta) => hiddenOr(rowOf(meta), v, (x) => formatNum(x, lang)) } },
    { name: 'peerAverage', label: t(`${NS}.col.peerAverage`), options: { sort: false, filter: false, customBodyRender: (v) => (v === null || v === undefined ? dash : formatNum(v, lang)) } },
    {
      name: 'deviation',
      label: t(`${NS}.col.deviation`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (v === null || v === undefined) {
            return r.deviationReason ? <Muted data-reason={r.managerEmployeeId}>{t(`${NS}.reason.${r.deviationReason}`)}</Muted> : dash;
          }
          return formatSigned(v, lang);
        },
      },
    },
    { name: 'highRate', label: t(`${NS}.col.highRate`), options: { sort: false, filter: false, customBodyRender: (v, meta) => hiddenOr(rowOf(meta), v, (x) => `${formatNum(x, lang)}%`) } },
    { name: 'stdDev', label: t(`${NS}.col.stdDev`), options: { sort: false, filter: false, customBodyRender: (v, meta) => hiddenOr(rowOf(meta), v, (x) => formatNum(x, lang)) } },
    {
      name: 'high',
      label: t(`${NS}.col.bands`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          return hiddenOr(r, v, () => t(`${NS}.bands.value`, {
            high: formatNum(r.high, lang, 0), mid: formatNum(r.mid, lang, 0), low: formatNum(r.low, lang, 0),
          }));
        },
      },
    },
    ...(calibrationApplicable ? [{
      name: 'calibrationShift',
      label: t(`${NS}.col.calibrationShift`),
      options: { sort: false, filter: false, customBodyRender: (v) => (v === null || v === undefined ? dash : formatSigned(v, lang)) },
    }] : []),
    { name: 'feedbackCoverage', label: t(`${NS}.col.feedbackCoverage`), options: { sort: false, filter: false, customBodyRender: (v, meta) => hiddenOr(rowOf(meta), v, (x) => `${formatNum(x, lang)}%`) } },
    { name: 'onTimeRate', label: t(`${NS}.col.onTime`), options: { sort: false, filter: false, customBodyRender: (v) => (v === null || v === undefined ? dash : `${formatNum(v, lang)}%`) } },
    { name: 'overdueTasks', label: t(`${NS}.col.overdue`), options: { sort: false, filter: false, customBodyRender: (v) => formatNum(v, lang, 0) } },
    {
      name: 'reviewStatus',
      label: t(`${NS}.col.reviewStatus`),
      options: { sort: false, filter: false, customBodyRender: (v) => <Badge $tone={REVIEW_TONE[v]}>{t(`${NS}.reviewStatus.${v || 'none'}`)}</Badge> },
    },
    {
      name: 'signals',
      label: t(`${NS}.col.signals`),
      options: { sort: false, filter: false, customBodyRender: (v) => ((v || []).length ? (v || []).map((s) => t(`${NS}.signal.${s}`)).join(', ') : dash) },
    },
    {
      name: 'managerEmployeeId',
      label: t(`${NS}.col.actions`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          const actions = [
            {
              type: 'open', label: t(`${NS}.rowAction.teamGaps`),
              onClick: () => history.push(withFilters(REVIEW_GAPS_PATH, { ...filters, managerEmployeeId: r.managerEmployeeId, includeDescendants: false })),
            },
            { type: 'scorecard', label: t(`${NS}.rowAction.calibration`), onClick: () => history.push(CALIBRATION_PATH) },
            { type: 'decide', label: t(`${NS}.rowAction.review`), onClick: () => setReviewFor(r) },
          ];
          return <OptionsMenu row={r} actions={actions} />;
        },
      },
    },
  ];

  return (
    <Wrap data-panel="managers">
      <Note>{t(`${NS}.list.separationNote`)}</Note>
      {panel.error ? (
        <>
          <LoadErrorBanner message={t('performance.operations.panel.error')} />
          <Button variant="outline" onClick={panel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
        </>
      ) : null}
      <DataTable
        title={t(`${NS}.list.title`)}
        fetching={panel.loading}
        columns={columns}
        data={rows}
        totalRowCount={total}
        pageIndex={table.pageNumber - 1}
        pageSize={table.pageSize}
        onTableChanged={onTableChanged}
        customIdSelector="managerEmployeeId"
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={(
          <Button variant="outline" onClick={handleExport} disabled={exporting || total === 0} data-action="export">
            <Download size={14} />
            {' '}
            {exporting ? t('common.loading') : t(`${NS}.list.export`)}
          </Button>
        )}
      />
      <GenericFormDrawer
        open={!!reviewFor}
        onClose={() => setReviewFor(null)}
        title={t(`${NS}.review.drawerTitle`)}
        schema={reviewSchema}
        initialValues={reviewInitial}
        submitLabel={t(`${NS}.review.save`)}
        onSave={async (values) => {
          const ok = await createReviewRecord({
            periodId: filters.periodId,
            managerEmployeeId: reviewFor.managerEmployeeId,
            title: values.title,
            ownerEmployeeId: values.ownerEmployeeId,
            dueDate: values.dueDate,
            description: values.description,
          });
          if (ok) { setReviewFor(null); panel.retry(); }
          return ok;
        }}
      />
    </Wrap>
  );
}
